import { ACCOUNT_PROVIDERS, HubCurrencies } from "@letsbit/malcolmlb";
import { FormattedMessage } from "react-intl";
import { messages } from "./messages";
import { useMemo } from "react";
import { useFormatter } from "hooks/utils/useFormatter";
import { BymaActivitiesScroll } from "components/BymaActivitiesScroll";
import { MarketPositionSymbolProps } from "./types";
import LoadingAnimation from "components/LoadingAnimation";
import AlpacaActivitiesScroll from "components/AlpacaActivitiesScroll";
import { useCurrencies } from "hooks/user/useCurrencies";

export const MarketPositionSymbol: React.FC<MarketPositionSymbolProps> = ({
  positions,
  symbol,
  memberUid,
  provider,
}) => {
  const { formatValue } = useFormatter();
  const { data: currencies } = useCurrencies();

  const ActivitiesScroll =
    provider === ACCOUNT_PROVIDERS.ESCO
      ? BymaActivitiesScroll
      : AlpacaActivitiesScroll;

  const { position, totalVolumeWithoutNegatives, currenciesObj } =
    useMemo(() => {
      const totalVolumeWithoutNegatives = positions.reduce((acc, position) => {
        return acc + Math.max(+position.market_value, 0);
      }, 0);

      const position = positions.find((position) => position.symbol === symbol);

      const currenciesObj = currencies?.reduce(
        (acc, currency) => {
          acc[currency.id] = currency;
          return acc;
        },
        {} as Record<string, HubCurrencies>,
      );

      return {
        position,
        totalVolumeWithoutNegatives,
        currenciesObj,
      };
    }, [symbol, positions]);

  return !position ? (
    <LoadingAnimation />
  ) : (
    <div className="h-full w-full pr-2">
      <ActivitiesScroll
        headerSticky={<FormattedMessage {...messages.lastMovements} />}
        memberUid={memberUid}
        header={
          <div className="flex w-full flex-col">
            <div className="mt-4 grid grid-cols-5 font-['Tomato_Grotesk']">
              <div className="col-span-2 flex flex-col gap-2">
                <h3 className="text-xl font-bold text-[--milkylb-color-neutral-600]">
                  <FormattedMessage {...messages.totalVolume} />
                </h3>
                <h2 className="text-3xl font-bold text-[--milkylb-color-neutral-800]">
                  ${formatValue((+position.market_value).toString())}{" "}
                  {currenciesObj?.[
                    position.price_currency
                  ]?.ticker_id.toUpperCase()}
                </h2>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm text-[--milkylb-color-neutral-400]">
                  <FormattedMessage {...messages.availableVolume} />
                </label>
                <h4 className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
                  {formatValue(position.qty_available.toString())}
                </h4>
              </div>
              {position.collateral_qty && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.collateralVolume} />
                  </label>
                  <h4 className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
                    {formatValue(position.collateral_qty.toString())}
                  </h4>
                </div>
              )}
              {position.guarantee_amount && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.guaranteedVolume} />
                  </label>
                  <h4 className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
                    {formatValue(position.guarantee_amount.toString())}
                  </h4>
                </div>
              )}
            </div>
            <div className="mb-8 mt-4 grid grid-cols-5 font-['Tomato_Grotesk']">
              <div className="col-span-2 flex flex-col gap-2">
                <label className="text-sm text-[--milkylb-color-neutral-400]">
                  <FormattedMessage {...messages.priceLabel} />
                </label>
                <h4 className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
                  $ {formatValue(position.current_price.toString())}{" "}
                  {currenciesObj?.[
                    position.price_currency
                  ]?.ticker_id.toUpperCase()}
                </h4>
              </div>
              {position.pending_qty_24hrs && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-[--milkylb-color-neutral-400]">
                    <FormattedMessage {...messages.thisDayLabel} />
                  </label>
                  <h4 className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
                    {formatValue(position.pending_qty_24hrs?.toString() ?? "0")}
                  </h4>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="text-sm text-[--milkylb-color-neutral-400]">
                  <FormattedMessage {...messages.percentageLabel} />
                </label>
                <h4 className="text-2xl font-bold text-[--milkylb-color-neutral-600]">
                  {formatValue(
                    (
                      (+position.market_value / totalVolumeWithoutNegatives) *
                      100
                    ).toString(),
                  )}
                  %
                </h4>
              </div>
            </div>
          </div>
        }
        initialFilter={{
          provider: provider,
          assets: [symbol ?? ""],
        }}
      />
    </div>
  );
};
